/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call */
import { useEffect } from 'react';

import { AppBar, Toolbar, Box, Stack, Container, SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

import { useAppContext, useFacilityContext } from '@/context';
import { App, ContentTile, BffFacility, HeaderMainNav, NavigationGroup } from '@/types/generated';
import { OfficeInfoType } from '@/services/useFacilities';
import { clickContext, isPathMatched } from '@/utils';
import TAGLink from '@/components/TAGLink';

import HeaderActions from './HeaderActions';
import { HeaderDrawer } from './HeaderDrawer';
import { MenuDrawer } from './MenuDrawer';
import { LocationDrawer } from './LocationDrawer';
import { useHeaderDrawer } from './useHeaderDrawer';
import { useMenuNavigation } from './useMenuNavigation';
import { useLocationManagement } from './useLocationManagement';
import { useAnalyticsTracking } from './useAnalyticsTracking';
import { useScrollDirection } from './useScrollDirection';
import { headerStyles } from './Header.styles';
import CustomIconButton from './UI/Button/CustomIconButton';
import MobileFooterActions from './MobileFooterActions';

export type ExtendedFacility = BffFacility &
	OfficeInfoType & {
		distanceInMiles?: number;
	};

interface HeaderProps {
	headerData: App['header'];
	logo: App['logo'];
	/**
	 * Paths where the sticky mobile footer actions should not be rendered.
	 * Supports exact paths (`/dts/`) and wildcards (`/mottoaligners/*`).
	 */
	disableStickyFooterPaths?: Array<string | null | undefined> | null;
}

const invokeInvocaScript = () => {
	setTimeout(() => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
		(globalThis as any)?.Invoca?.PNAPI?.run();
	}, 100);
};

const HeaderAspen = ({ headerData, logo, disableStickyFooterPaths }: HeaderProps) => {
	const phoneNumber = '(800) 277-3633';

	const { config } = useAppContext();
	const { officeInfo: selectedOffice } = useFacilityContext();
	const pathname = usePathname();

	const hideStickyFooter = isPathMatched(pathname, disableStickyFooterPaths);

	// sticky header hide/show behavior
	const scrollDirection = useScrollDirection({ threshold: 10, topOffset: 100 });

	const { isDrawerOpen, drawerMode, openMenuDrawer, openLocationDrawer, closeDrawer } = useHeaderDrawer();

	const {
		currentMenuLevel,
		selectedMainItem,
		selectedSubItem,
		handleMainItemClick,
		handleSubItemClick,
		handleBackToMain,
		handleBackToSubmenu,
		resetNavigation,
	} = useMenuNavigation();

	const {
		selectedOfficeCode,
		setSelectedOfficeCode,
		searchError,
		homeUrl,
		displayedOffices,
		handleSetAsMyLocationClick,
		handleLocationSearch,
		resetLocationState,
	} = useLocationManagement();

	const { handleScheduleClick, handleNavigationClick, handleTileClick, trackFacilitySet, trackPhoneNumber } =
		useAnalyticsTracking();

	const handleDrawerClose = () => {
		closeDrawer();
		resetNavigation();
		resetLocationState();
	};

	const handleSetLocationAndClose = () => {
		handleSetAsMyLocationClick(trackFacilitySet);
		handleDrawerClose();
	};

	const handleExploreTileClick = (tile: ContentTile, href: string) => {
		handleTileClick(tile, href);
		handleDrawerClose();
	};

	// Update CSS custom property for header visibility offset
	useEffect(() => {
		const root = document.documentElement;
		if (scrollDirection === 'down') {
			root.style.setProperty('--header-offset', '0px');
		} else {
			root.style.setProperty('--header-offset', 'var(--header-height)');
		}
	}, [scrollDirection]);

	useEffect(() => {
		trackPhoneNumber(phoneNumber);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phoneNumber, selectedOffice?.code]);

	useEffect(() => {
		if (isDrawerOpen) invokeInvocaScript();
	}, [isDrawerOpen]);

	const isHidden = scrollDirection === 'down';
	const isVisible = scrollDirection === 'up' || scrollDirection === null;

	return (
		<>
			<AppBar
				position="sticky"
				sx={
					[
						headerStyles.headerAppBar,
						isHidden && headerStyles.headerHidden,
						isVisible && headerStyles.headerVisible,
					] as SxProps<Theme>
				}
			>
				<Container maxWidth={false} sx={headerStyles.container}>
					<Toolbar sx={headerStyles.toolbar}>
						{/* Left: menu + desktop logo */}
						<Stack direction="row" spacing={2} alignItems="center">
							<CustomIconButton
								size="small"
								color="primary"
								onClick={openMenuDrawer}
								aria-label="Menu"
								sx={headerStyles.menuButton}
							>
								<MenuIcon sx={headerStyles.menuIcon} />
							</CustomIconButton>

							<Box sx={headerStyles.desktopLogo}>
								{logo?.url && (
									<TAGLink
										href={homeUrl}
										title="Aspen Logo"
										analyticsContext={clickContext({ brand: config.name, type: 'logoClick' })}
										dataTestId="header_logo_aspen2_desktop"
									>
										<Image
											src={logo.url}
											alt={logo.description || 'Aspen Logo'}
											width={180}
											height={36}
											style={{ height: 'auto' }}
										/>
									</TAGLink>
								)}
							</Box>
						</Stack>

						{/* Mobile logo */}
						<Box sx={headerStyles.mobileLogo}>
							{logo?.url && (
								<TAGLink
									href={homeUrl}
									title="Aspen Logo"
									analyticsContext={clickContext({ brand: config.name, type: 'logoClick' })}
									dataTestId="header_logo_aspen2_mobile"
								>
									<Image
										src={logo.url}
										alt={logo.description || 'Aspen Logo'}
										width={180}
										height={36}
										style={{ height: 'auto' }}
									/>
								</TAGLink>
							)}
						</Box>

						{/* Desktop actions */}
						<HeaderActions
							setShowLocationsDrawer={openLocationDrawer}
							phoneNumber={phoneNumber}
							onScheduleClick={handleScheduleClick}
						/>

						{/* Drawer wrapper */}
						<HeaderDrawer
							isOpen={isDrawerOpen}
							drawerMode={drawerMode}
							onClose={handleDrawerClose}
							onBackToMenu={openMenuDrawer}
						>
							{drawerMode === 'menu' && (
								<MenuDrawer
									selectedOffice={selectedOffice as ExtendedFacility}
									currentMenuLevel={currentMenuLevel}
									selectedMainItem={selectedMainItem as HeaderMainNav}
									selectedSubItem={selectedSubItem as NavigationGroup}
									headerData={headerData}
									phoneNumber={phoneNumber}
									homeUrl={homeUrl}
									onChangeLocationClick={openLocationDrawer}
									onBackToMain={handleBackToMain}
									onBackToSubmenu={handleBackToSubmenu}
									onMainItemClick={handleMainItemClick}
									onSubItemClick={handleSubItemClick}
									onNavigationClick={handleNavigationClick}
									onTileClick={handleExploreTileClick}
									onScheduleClick={handleScheduleClick}
								/>
							)}
							{drawerMode === 'locations' && (
								<LocationDrawer
									displayedOffices={(displayedOffices as BffFacility[]) ?? []}
									selectedOfficeCode={selectedOfficeCode}
									setSelectedOfficeCode={setSelectedOfficeCode}
									searchError={searchError}
									selectedOffice={selectedOffice as ExtendedFacility}
									onLocationSearch={(query) => {
										void handleLocationSearch(query);
									}}
									onSetAsMyLocationClick={handleSetLocationAndClose}
									onScheduleClick={handleScheduleClick}
								/>
							)}
						</HeaderDrawer>
					</Toolbar>
				</Container>
			</AppBar>

			{!hideStickyFooter && (
				<MobileFooterActions phoneNumber={phoneNumber} onScheduleClick={handleScheduleClick} />
			)}
		</>
	);
};

export default HeaderAspen;
